import { useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Check, Copy, ImageIcon, Lock, Sparkles, Loader2 } from "lucide-react";
import { LocalProductPhoto, PrimaryImageButton, ProductPhoto } from '@/components/ProductPhoto';
import { useResolvedImages, type ResolvedPhoto } from '@/api/produktbilder';
import { Panel } from "@/components/Panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PortalBadge } from "@/components/PortalBadge";
import { EditableField } from "@/components/EditableField";
import { BilderBlock } from "../bilder/BilderBlock";
import { BildDialog } from "../bilder/BildDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ApiError } from "@/api/client";
import {
  useProductKnowledge,
  useProductKnowledgeEvidence,
  useProductKnowledgeImages,
  useListingHtml,
  useHarvestKnowledge,
  useEnrichKnowledge,
  useRestoreKnowledgeVersion,
  useUpdateProductKnowledge,
  EnrichLockedError,
  fetchProductKnowledgeImage,
  type EvidenceImageRef,
  type EvidenceCard,
  type CanonicalImage,
  type ListingHtmlIdentity,
  type KnowledgeStatus,
} from "@/api/productKnowledge";

const LISTING_HTML_PORTALS = new Set(["ebay", "shopify", "metro"]);
const EVIDENCE_CHIP_LIMIT = 12;

type ListingHtmlDialogTarget = ListingHtmlIdentity & { mode: "design" | "source" };

function listingHtmlIdentityFor(evidence: EvidenceCard): ListingHtmlIdentity | null {
  const portal = evidence.portal?.toLowerCase() ?? "";
  if (evidence.hasListingHtml !== true || !LISTING_HTML_PORTALS.has(portal) || !evidence.accountKey || !evidence.listingRef) {
    return null;
  }
  return { portal, accountKey: evidence.accountKey, listingRef: evidence.listingRef };
}

function fmtDatum(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ status }: { status: KnowledgeStatus }) {
  if (status === "locked") {
    return (
      <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn gap-1">
        <Lock className="size-3" aria-hidden />
        bearbeitet (gesperrt)
      </Badge>
    );
  }
  if (status === "auto") {
    return (
      <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary gap-1">
        <Sparkles className="size-3" aria-hidden />
        automatisch befüllt
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-line text-muted gap-1">
      noch nicht angereichert
    </Badge>
  );
}

function EvidenceImagePlaceholder() {
  return (
    <div
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground"
      data-testid="evidence-image-placeholder"
      aria-label="Keine Bildvorschau verfügbar"
    >
      <ImageIcon className="size-5" aria-hidden />
    </div>
  );
}

/** Gemeinsamer Bild-Loader fuer Evidenz und Galerie: Proxy-Blob, bei Fehler einmal die
 *  HTTPS-Portal-URL, danach Platzhalter. Ohne Hash direkt die Portal-URL. Object-URLs
 *  werden bei Quellenwechsel, Unmount und Dekodierfehler freigegeben. */
function ProxiedImage({
  fileHash,
  fallbackUrl,
  alt,
  className,
  placeholder,
  localOnly = false,
}: {
  fileHash: string | null;
  fallbackUrl?: string | null;
  alt: string;
  className?: string;
  placeholder: ReactNode;
  localOnly?: boolean;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  let safeFallbackUrl: string | null = null;
  try {
    if (fallbackUrl && new URL(fallbackUrl).protocol === "https:") safeFallbackUrl = fallbackUrl;
  } catch {
    // Ungueltige oder relative URLs bleiben beim Platzhalter.
  }

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setSrc(null);
    setFailed(false);
    setFallbackFailed(false);

    if (fileHash) {
      void (localOnly ? fetchProductKnowledgeImage(fileHash, true) : fetchProductKnowledgeImage(fileHash))
        .then((blob) => {
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          objectUrlRef.current = objectUrl;
          setSrc(objectUrl);
        })
        .catch(() => {
          if (active) setFailed(true);
        });
    }

    return () => {
      active = false;
      if (objectUrl && objectUrlRef.current === objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrlRef.current = null;
      }
    };
  }, [fileHash, safeFallbackUrl, localOnly]);

  const useFallback = !fileHash || failed;
  function handleImageError() {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setSrc(null);
    if (useFallback) setFallbackFailed(true);
    else setFailed(true);
  }

  const imageSrc = useFallback ? safeFallbackUrl : src;
  if (fallbackFailed || !imageSrc) return <>{placeholder}</>;

  return (
    <img
      key={imageSrc}
      src={imageSrc}
      alt={alt}
      className={className}
      referrerPolicy={useFallback ? "no-referrer" : undefined}
      loading={useFallback ? "lazy" : undefined}
      onError={handleImageError}
    />
  );
}

function EvidenceThumbnail({ image, alt, legacy, photo }: { image: EvidenceImageRef; alt: string; legacy: boolean; photo?: ResolvedPhoto }) {
  if (!legacy && photo) return <LocalProductPhoto photo={photo} alt={alt} className="h-14 w-14 shrink-0" />;
  return (
    <ProxiedImage
      fileHash={image.fileHash}
      fallbackUrl={legacy ? image.url : null}
      localOnly={!legacy}
      alt={alt}
      className="h-14 w-14 shrink-0 rounded-md border bg-muted object-cover"
      placeholder={<EvidenceImagePlaceholder />}
    />
  );
}

/** Eine Kachel der Unikat-Galerie: entdoppeltes Produktbild ueber denselben Proxy-Loader,
 *  darunter der Google-konforme SEO-Dateiname (monospaced, umbruchsicher) und die Portale,
 *  in denen dieses Bild vorkommt — als dieselben PortalBadges wie im uebrigen Reiter. */
function CanonicalImageTile({ image, sku, legacy, onUebernehmen }: { image: CanonicalImage; sku: string; legacy: boolean; onUebernehmen: () => void }) {
  const photo: ResolvedPhoto = { ...image, publicUrl: image.publicUrl ?? '', sourceSku: image.sourceSku ?? sku, match: image.match ?? 'family',
    variantSku: image.variantSku ?? null, usageId: image.usageId ?? null };
  return (
    <figure className="flex min-w-0 flex-col gap-1.5">
      {legacy ? <ProxiedImage fileHash={image.fileHash} fallbackUrl={image.originalUrl} alt={image.altText ?? image.seoName ?? ''}
        className="aspect-square w-full rounded-md border border-line bg-muted object-cover" placeholder={<EvidenceImagePlaceholder />} /> : <LocalProductPhoto
        photo={photo}
        alt={image.altText ?? image.seoName ?? ""}
        className="aspect-square w-full rounded-md border border-line bg-muted object-cover"
      />}
      {image.seoName && (
        <figcaption className="break-all font-mono text-[11px] text-ink-2">{image.seoName}</figcaption>
      )}
      {image.sourcePortals.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {image.sourcePortals.map((portal) => (
            <PortalBadge key={portal} portal={portal} />
          ))}
        </div>
      )}
      {image.origin === 'own' && <Button type="button" variant="outline" size="sm" className="h-auto whitespace-normal text-xs" onClick={onUebernehmen}>In eigenen Satz übernehmen</Button>}
      <PrimaryImageButton sku={sku} photo={photo} />
      {photo.variantSku && <span className="text-xs text-ink-2">Variante: {photo.variantSku}</span>}
      {photo.sourceSku !== sku && <span className="text-xs text-ink-2">Quelle: {photo.sourceSku}</span>}
    </figure>
  );
}

function EvidenceCardItem({
  evidence,
  onOpenListingHtml,
  legacy,
  photos,
}: {
  evidence: EvidenceCard;
  legacy: boolean;
  photos: Map<string, ResolvedPhoto>;
  onOpenListingHtml: (target: ListingHtmlDialogTarget) => void;
}) {
  const [showAllChips, setShowAllChips] = useState(false);
  const listingHtmlIdentity = listingHtmlIdentityFor(evidence);
  const chips = [
    ...evidence.keywordsRaw.map((keyword, index) => ({
      key: `keyword-${keyword}-${index}`,
      kind: "keyword" as const,
      label: keyword,
    })),
    ...evidence.attributes.map((attribute, index) => ({
      key: `attribute-${attribute.type}-${attribute.value}-${index}`,
      kind: "attribute" as const,
      label: `${attribute.type ? `${attribute.type}: ` : ""}${attribute.value}`,
    })),
  ];
  const hiddenChipCount = Math.max(0, chips.length - EVIDENCE_CHIP_LIMIT);
  const visibleChips = showAllChips ? chips : chips.slice(0, EVIDENCE_CHIP_LIMIT);

  return (
    <div className="isolate flex min-w-0 flex-col gap-2 overflow-hidden rounded-md border border-line bg-panel-2/30 p-3">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <PortalBadge portal={evidence.portal} />
          {evidence.accountKey && <span className="truncate font-mono text-xs text-muted">{evidence.accountKey}</span>}
        </div>
        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
          geerntet am {fmtDatum(evidence.harvestedAt)}
        </span>
      </div>
      {/* overflow-hidden zusaetzlich zum line-clamp: Prod-Befund 2026-07-21 zeigte Text,
          der ueber die 3-Zeilen-Grenze hinaus in die Chip-Zone malte — explizites Clipping
          direkt am geclampten Element wirkt auch, falls die overflow-Deklaration des
          line-clamp-Utilities im Bundle verloren geht oder ueberschrieben wird. */}
      <div className="line-clamp-2 overflow-hidden font-medium text-ink">{evidence.title ?? "—"}</div>
      {evidence.descriptionPlain && (
        <div className="line-clamp-3 overflow-hidden text-sm text-muted-foreground">{evidence.descriptionPlain}</div>
      )}
      {chips.length > 0 && (
        <div className="flex min-w-0 flex-wrap gap-1">
          {visibleChips.map((chip) => (
            <Badge
              key={chip.key}
              variant={chip.kind === "keyword" ? "secondary" : "outline"}
              className="h-auto w-auto max-w-full shrink whitespace-normal break-words justify-start text-left text-[10px]"
            >
              {chip.label}
            </Badge>
          ))}
          {hiddenChipCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto px-1.5 py-0.5 text-[10px] text-muted"
              aria-expanded={showAllChips}
              onClick={() => setShowAllChips((current) => !current)}
            >
              {showAllChips ? "weniger anzeigen" : `+${hiddenChipCount} weitere`}
            </Button>
          )}
        </div>
      )}
      {evidence.images.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {evidence.images.slice(0, 6).map((image, index) => (
            <EvidenceThumbnail
              key={`${image.fileHash ?? "missing"}-${index}`}
              image={image}
              photo={image.fileHash ? photos.get(image.fileHash) : undefined}
              legacy={legacy}
              alt={evidence.title ?? "Produktbild"}
            />
          ))}
        </div>
      )}
      {listingHtmlIdentity && (
        <div className="flex self-start gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-xs"
            onClick={() => onOpenListingHtml({ ...listingHtmlIdentity, mode: "design" })}
          >
            Original-Design ansehen
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-xs"
            onClick={() => onOpenListingHtml({ ...listingHtmlIdentity, mode: "source" })}
          >
            HTML
          </Button>
        </div>
      )}
    </div>
  );
}

/** Reiter „Inhalt“ des Artikel-Details (seit 12.10.2026 mit Produkt-Wissen zusammengelegt).
 *  Reihenfolge: Wissen oben (Produktverstaendnis, KI-Analyse, Evidenz), dann die gepflegte
 *  Wahrheit als Einschub `children` (Keywords/Merkmale/FAQs), dann `bilder` und die
 *  Unikat-Galerie, zuletzt Versionen. Der Bilder-Block verwaltet Uploads und Verwendungen; andere
 *  Portale lesen dieselben Endpunkte. Ein Ladefehler des Wissens blendet nur die
 *  Wissens-Bloecke aus, nie die Einschuebe. */
export default function ProduktWissenTab({
  sku,
  artikelname = sku,
  children,
  bilder,
}: {
  sku: string;
  artikelname?: string;
  children?: ReactNode;
  bilder?: ReactNode;
}) {
  const { data: knowledge, isLoading, error } = useProductKnowledge(sku);
  const { data: evidence, isLoading: evidenceLoading } = useProductKnowledgeEvidence(sku);
  const resolved = useResolvedImages([sku]);
  const legacyImages = useProductKnowledgeImages(sku, resolved.data?.enabled === false);
  const photosByHash = new Map(resolved.data?.results[0]?.gallery.map(photo => [photo.fileHash, photo]));
  const images = resolved.data?.enabled === false ? legacyImages : {
    isLoading: resolved.isLoading, data: resolved.data?.results[0]?.gallery,
  };
  const harvest = useHarvestKnowledge(sku);
  const enrich = useEnrichKnowledge(sku);
  const restore = useRestoreKnowledgeVersion(sku);
  const update = useUpdateProductKnowledge(sku);

  const [lockDialog, setLockDialog] = useState<{ lockedBy: string | null; lockedAt: string | null } | null>(null);
  const [archivBild, setArchivBild] = useState<CanonicalImage | null>(null);
  const [listingHtmlTarget, setListingHtmlTarget] = useState<ListingHtmlDialogTarget | null>(null);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const copyResetTimerRef = useRef<number | null>(null);
  const listingHtmlIdentity: ListingHtmlIdentity | null = listingHtmlTarget
    ? {
        portal: listingHtmlTarget.portal,
        accountKey: listingHtmlTarget.accountKey,
        listingRef: listingHtmlTarget.listingRef,
      }
    : null;
  const listingHtml = useListingHtml(listingHtmlIdentity);

  useEffect(
    () => () => {
      if (copyResetTimerRef.current !== null) window.clearTimeout(copyResetTimerRef.current);
    },
    [],
  );

  if (isLoading || error || !knowledge) {
    return (
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <div className="text-sm text-muted">Produkt-Wissen wird geladen…</div>
        ) : (
          <div className="text-sm text-coral">
            Produkt-Wissen konnte nicht geladen werden. Das heisst <strong>nicht</strong>, dass keins existiert.
          </div>
        )}
        {children}
        {bilder}
        <BilderBlock sku={sku} artikelname={artikelname} />
      </div>
    );
  }

  function clearCopyResetTimer() {
    if (copyResetTimerRef.current === null) return;
    window.clearTimeout(copyResetTimerRef.current);
    copyResetTimerRef.current = null;
  }

  function handleOpenListingHtml(target: ListingHtmlDialogTarget) {
    clearCopyResetTimer();
    setCopiedHtml(false);
    setListingHtmlTarget(target);
  }

  function handleCloseListingHtml() {
    clearCopyResetTimer();
    setCopiedHtml(false);
    setListingHtmlTarget(null);
  }

  async function handleCopyListingHtml() {
    const rawHtml = listingHtml.data?.rawHtml;
    if (rawHtml === undefined) return;

    clearCopyResetTimer();
    setCopiedHtml(false);
    try {
      await navigator.clipboard.writeText(rawHtml);
      setCopiedHtml(true);
      toast.success("HTML kopiert");
      copyResetTimerRef.current = window.setTimeout(() => {
        setCopiedHtml(false);
        copyResetTimerRef.current = null;
      }, 2_000);
    } catch {
      setCopiedHtml(false);
      toast.error("HTML konnte nicht kopiert werden");
    }
  }

  function handleHarvest() {
    harvest.mutate(undefined, {
      onSuccess: (res) => {
        const s = res.result;
        toast.success(`Ernte abgeschlossen: ${s.inserted} neu, ${s.updated} aktualisiert, ${s.unchanged} unveraendert.`);
        if (s.failed.length > 0) toast.warning(`${s.failed.length} Quelle(n) fehlgeschlagen — siehe Log.`);
      },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "Ernte fehlgeschlagen"),
    });
  }

  function handleEnrichClick() {
    // Bereits bekannter Lock-Stand → Dialog direkt zeigen, keine unnoetige Anfrage.
    if (knowledge!.status === "locked") {
      setLockDialog({ lockedBy: knowledge!.lockedBy, lockedAt: knowledge!.lockedAt });
      return;
    }
    enrich.mutate(
      { force: false },
      {
        onSuccess: () => toast.success("Anreicherung abgeschlossen."),
        onError: (e) => {
          // Race: Datensatz wurde zwischen Laden und Klick gesperrt — Dialog dann
          // reaktiv aus dem Fehler befuellen (lockedBy/lockedAt aus ApiError.details).
          if (e instanceof EnrichLockedError) {
            setLockDialog({ lockedBy: e.lockedBy, lockedAt: e.lockedAt });
            return;
          }
          toast.error(e instanceof ApiError ? e.message : "Anreicherung fehlgeschlagen");
        },
      },
    );
  }

  function handleConfirmForceEnrich() {
    setLockDialog(null);
    enrich.mutate(
      { force: true },
      {
        onSuccess: () => toast.success("Anreicherung abgeschlossen (Sperre ueberschrieben, Version gesichert)."),
        onError: (e) => toast.error(e instanceof ApiError ? e.message : "Anreicherung fehlgeschlagen"),
      },
    );
  }

  function handleRestore(versionNo: number) {
    if (!window.confirm(`Version ${versionNo} wiederherstellen? Der aktuelle Stand wird dabei ersetzt.`)) return;
    restore.mutate(versionNo, {
      onSuccess: () => toast.success(`Version ${versionNo} wiederhergestellt.`),
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "Wiederherstellen fehlgeschlagen"),
    });
  }

  async function saveField(patch: Parameters<typeof update.mutateAsync>[0]) {
    await update.mutateAsync(patch);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-primary" aria-hidden />
          <h2 className="font-display font-semibold text-base text-ink">Produkt-Wissen</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">Zuletzt angereichert: {fmtDatum(knowledge.enrichedAt)}</span>
          <StatusBadge status={knowledge.status} />
          <Button variant="outline" size="sm" disabled={harvest.isPending} onClick={handleHarvest}>
            {harvest.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
            {harvest.isPending ? "Läuft…" : "Jetzt neu ernten"}
          </Button>
          <Button size="sm" disabled={enrich.isPending} onClick={handleEnrichClick}>
            {enrich.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
            {enrich.isPending ? "Läuft…" : "Neu anreichern"}
          </Button>
        </div>
      </div>

      <Panel title="Produktverständnis" subtitle="Operator-Master — ueberschreibt automatisch befuellte Werte, Backend sperrt den Datensatz.">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-3">
            <EditableField
              type="text"
              label="Produktart"
              value={knowledge.productType}
              onSave={(v) => saveField({ product_type: v as string | null })}
            />
            <EditableField
              type="text"
              label="Material"
              value={knowledge.material}
              onSave={(v) => saveField({ material: v as string | null })}
            />
          </div>
          <div className="flex flex-col gap-3">
            <EditableField
              type="text"
              label="Verwendungszweck"
              value={knowledge.useCase}
              onSave={(v) => saveField({ use_case: v as string | null })}
            />
            <EditableField
              type="text"
              label="Zielgruppe"
              value={knowledge.targetAudience}
              onSave={(v) => saveField({ target_audience: v as string | null })}
            />
          </div>
          <div className="lg:col-span-2">
            <EditableField
              type="textarea"
              label="Verkaufsargumente (eine Zeile je Argument)"
              value={knowledge.sellingPoints.join("\n")}
              onSave={(v) =>
                saveField({
                  selling_points: String(v ?? "")
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
        </div>
      </Panel>

      <Panel
        title="KI-Analyse"
        subtitle="Begründung der Keywords, KI-Merkmale und Titelempfehlungen je Portal. Die Keywords selbst stehen unten im Block Keywords."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-2">Keyword-Analyse</div>
            {knowledge.keywordsAnalysis.length === 0 ? (
              <div className="text-sm text-muted-2 italic">Noch keine Keyword-Analyse vorhanden.</div>
            ) : (
              <ul className="flex flex-col gap-2">
                {knowledge.keywordsAnalysis.map((k, i) => (
                  <li key={`${k.keyword}-${i}`} className="text-sm">
                    <span className="font-mono font-medium text-ink">{k.keyword}</span>
                    <span className="text-ink-2"> — </span>
                    <span className="italic text-muted">{k.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-2">Titelempfehlungen je Portal</div>
            {knowledge.recommendedTitles.length === 0 ? (
              <div className="text-sm text-muted-2 italic">Noch keine Titelempfehlungen vorhanden.</div>
            ) : (
              <ul className="flex flex-col gap-2">
                {knowledge.recommendedTitles.map((t, i) => (
                  <li key={`${t.portal}-${i}`} className="flex items-start gap-2 text-sm">
                    <PortalBadge portal={t.portal} />
                    <span>{t.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-2">
              KI-Merkmale (aus geernteter Evidenz — nicht der Block „Merkmale")
            </div>
            {knowledge.aiCharacteristics.length === 0 ? (
              <div className="text-sm text-muted-2 italic">Noch keine KI-Merkmale vorhanden.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {knowledge.aiCharacteristics.map((c, i) => (
                  <Badge key={`${c.type}-${c.value}-${i}`} variant="secondary" className="text-[11px]">
                    {c.type ? `${c.type}: ` : ""}
                    {c.value ?? "—"}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </Panel>

      <Panel title="Evidenz-Übersicht" subtitle="Read-only — was je Portal/Konto zuletzt geerntet wurde.">
        {evidenceLoading ? (
          <div className="text-sm text-muted">Evidenz wird geladen…</div>
        ) : !evidence || evidence.length === 0 ? (
          <div className="text-sm text-muted">Noch keine Evidenz geerntet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {evidence.map((item, index) => (
              <EvidenceCardItem
                key={`${item.portal}-${item.accountKey}-${index}`}
                evidence={item}
                photos={photosByHash}
                legacy={resolved.data?.enabled === false}
                onOpenListingHtml={handleOpenListingHtml}
              />
            ))}
          </div>
        )}
      </Panel>

      {children}

      {bilder}

      <BilderBlock sku={sku} artikelname={artikelname} />

      <Panel
        title="Einmalige Produktbilder (portalübergreifend)"
        subtitle="Kuratierter Unikat-Satz — entdoppelte Produktbilder aller Portale, benannt nach SEO-Dateinamen."
      >
        {!!resolved.data?.results[0]?.components.length && <ProductPhoto images={resolved.data.results[0]} alt={artikelname} />}
        {resolved.error && <p role="alert" className="text-sm text-coral">{resolved.error.message}</p>}
        {images.isLoading ? (
          <div className="text-sm text-muted">Bilder werden geladen…</div>
        ) : (images.data?.length ?? 0) === 0 ? (
          <div className="text-sm text-muted">
            {evidence?.some((row) => row.images.some((image) => image.url))
              ? "Archivierung ausstehend"
              : "Noch keine lokal verfügbaren Bilder"}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {images.data!.map((image) => (
              <CanonicalImageTile key={`${image.fileHash}-${'sourceSku' in image ? image.sourceSku : ''}`} sku={sku} legacy={resolved.data?.enabled === false} image={image} onUebernehmen={() => setArchivBild(image)} />
            ))}
          </div>
        )}
      </Panel>

      {archivBild && <BildDialog key={archivBild.fileHash} sku={sku} artikelname={artikelname} archiv={archivBild} onClose={() => setArchivBild(null)} />}

      <Dialog open={listingHtmlTarget !== null} onOpenChange={(open) => !open && handleCloseListingHtml()}>
        <DialogContent className="h-[80vh] max-h-[80vh] grid-rows-[auto_minmax(0,1fr)] sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{listingHtmlTarget?.mode === "source" ? "HTML-Quelltext" : "Original-Design"}</DialogTitle>
            <DialogDescription>
              {listingHtmlTarget && (
                <>
                  Portal: {listingHtmlTarget.portal} · Konto: {listingHtmlTarget.accountKey} · Listing-Ref:{" "}
                  {listingHtmlTarget.listingRef}
                  {listingHtml.data && <> · geerntet am {fmtDatum(listingHtml.data.fetchedAt)}</>}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 overflow-hidden rounded-md border border-line bg-muted/20">
            {listingHtml.isLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-muted">Original-HTML wird geladen…</div>
            ) : listingHtml.error instanceof ApiError && listingHtml.error.status === 404 ? (
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted">
                Fuer dieses Angebot ist (noch) kein Original-HTML archiviert — naechste Ernte holt es.
              </div>
            ) : listingHtml.error ? (
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-coral">
                Original-HTML konnte nicht geladen werden.
              </div>
            ) : listingHtml.data ? (
              listingHtmlTarget?.mode === "source" ? (
                <div className="flex h-full min-h-0 flex-col gap-2 p-3">
                  <div className="flex shrink-0 justify-end">
                    <Button variant="outline" size="sm" onClick={() => void handleCopyListingHtml()}>
                      {copiedHtml ? (
                        <Check className="size-3.5" aria-hidden />
                      ) : (
                        <Copy className="size-3.5" aria-hidden />
                      )}
                      {copiedHtml ? "Kopiert" : "HTML kopieren"}
                    </Button>
                  </div>
                  <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words rounded-md bg-panel p-3 font-mono text-xs">{listingHtml.data.rawHtml}</pre>
                </div>
              ) : (
                <iframe
                  title={`Original-Design ${listingHtmlTarget?.portal ?? "Angebot"} ${listingHtmlTarget?.listingRef ?? ""}`}
                  className="h-full w-full bg-white"
                  sandbox=""
                  srcDoc={listingHtml.data.rawHtml}
                />
              )
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Panel title="Versionen" subtitle="Snapshot vor jeder Anreicherung/Wiederherstellung.">
        {knowledge.versions.length === 0 ? (
          <div className="text-sm text-muted">Noch keine Versionen vorhanden.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted text-left">
                <th className="pb-2">Version</th>
                <th className="pb-2">Auslöser</th>
                <th className="pb-2">Erstellt</th>
                <th className="pb-2">Von</th>
                <th className="pb-2 w-32" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line-2">
              {[...knowledge.versions].reverse().map((v) => (
                <tr key={v.versionNo} className="hover:bg-panel-2/40">
                  <td className="py-2 font-mono">#{v.versionNo}</td>
                  <td className="py-2">{v.reason}</td>
                  <td className="py-2 whitespace-nowrap">{fmtDatum(v.createdAt)}</td>
                  <td className="py-2">{v.createdBy}</td>
                  <td className="py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={restore.isPending}
                      onClick={() => handleRestore(v.versionNo)}
                    >
                      Wiederherstellen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={lockDialog !== null} onOpenChange={(open) => !open && setLockDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="size-4 text-warn" aria-hidden />
              Datensatz ist gesperrt
            </DialogTitle>
            <DialogDescription>
              {lockDialog?.lockedBy ?? "Ein Operator"} hat diesen Datensatz am {fmtDatum(lockDialog?.lockedAt ?? null)}{" "}
              manuell bearbeitet. Eine erneute Anreicherung ueberschreibt diese Bearbeitung — der aktuelle Stand wird
              vorher als neue Version gesichert und bleibt ueber „Wiederherstellen" abrufbar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialog(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleConfirmForceEnrich}>Trotzdem anreichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
